import type { Job } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import { toast } from 'react-toastify';

/** Intervalle entre deux lectures du Job360, en millisecondes. */
const INTERVALLE = 2000;

/** Au-delà, le suivi s'arrête : le job de l'API est coupé à 30 min. */
const DUREE_MAX = 35 * 60 * 1000;

const MESSAGE_SUIVI_INTERROMPU =
	'Le suivi de la mise à jour du statut Chorus a été interrompu : son résultat est visible en relançant la mise à jour.';

/**
 * Bilan de la mise à jour (résultat JSON du Job360) : son message, ou, d'une API qui ne le donne pas encore, le
 * nombre de factures mises à jour.
 */
const getMessageSucces = (resultat: string): string => {
	const bilan = JSON.parse(resultat ?? '{}') ?? {};

	return (
		bilan.message ??
		`Statut Chorus mis à jour : ${bilan.misesAJour ?? 0} facture(s).`
	);
};

const attendre = (millisecondes: number) =>
	new Promise((resolve) => {
		setTimeout(resolve, millisecondes);
	});

/**
 * Suit, en arrière-plan, la mise à jour du statut Chorus des factures d'un bordereau, lancée par l'API après sa
 * numérotation (Job360 identifiantJobChorus du résultat de la numérotation) : elle continue après la fermeture de
 * la modale. À sa fin, un toast de succès avec le bilan, ou d'erreur avec le bilan et chaque facture en échec, qui
 * reste affiché jusqu'à ce que l'utilisateur le ferme.
 */
const suivreMajChorus = async (identifiantJob: number): Promise<void> => {
	const fin = Date.now() + DUREE_MAX;

	try {
		while (Date.now() < fin) {
			await attendre(INTERVALLE);
			const job = await request.get<unknown, Job>(
				`/api/transverse/job360/${identifiantJob}`,
			);
			if (job.status === null || job.status === undefined) {
				continue;
			}

			if (job.status === 1) {
				toast.success(getMessageSucces(job.resultat));
			} else {
				toast.error(job.resultat, { autoClose: false });
			}
			return;
		}
	} catch {
		// lecture du job impossible : rien n'indique que la mise à jour a échoué
	}

	toast.warning(MESSAGE_SUIVI_INTERROMPU, { autoClose: false });
};

/**
 * Lance le suivi de la mise à jour Chorus si la numérotation (résultat JSON du Job360 terminé) en a lancé une.
 */
export const suivreMajChorusApresNumerotation = (
	jobNumerotation: Job,
): void => {
	let identifiantJobChorus: number | null = null;
	try {
		identifiantJobChorus =
			JSON.parse(jobNumerotation.resultat)?.identifiantJobChorus ?? null;
	} catch {
		return;
	}
	if (identifiantJobChorus !== null) {
		void suivreMajChorus(identifiantJobChorus);
	}
};

export default suivreMajChorus;
